import { aiOpenapiClient, unwrapResult } from '@/api/openapi-client';
import { env } from '@/env';
import type {
  AiAttachmentUpload,
  AiAttachmentUploadTicket,
  AiConversationThread,
  AiThreadConnection,
  AiThreadStatus,
  UpdateAiThreadBody,
} from '@restaurant/api-client';
import { validateUIMessages, type UIMessage } from 'ai';

export const aiKeys = {
  all: (restaurantId: string) => ['ai-assistant', restaurantId] as const,
  messages: (restaurantId: string, threadId: string) =>
    ['ai-assistant', restaurantId, 'messages', threadId] as const,
};

export const chatUrl = (restaurantId: string) => `${env.aiUrl}/restaurants/${restaurantId}/ai/chat`;

export async function listThreads(
  restaurantId: string,
  status: AiThreadStatus,
): Promise<AiThreadConnection> {
  return unwrapResult<AiThreadConnection>(
    await aiOpenapiClient.GET('/restaurants/{restaurantId}/ai/threads', {
      params: { path: { restaurantId }, query: { status, limit: 100 } },
    }),
  );
}

export async function createThread(restaurantId: string): Promise<AiConversationThread> {
  return unwrapResult<AiConversationThread>(
    await aiOpenapiClient.POST('/restaurants/{restaurantId}/ai/threads', {
      params: { path: { restaurantId } },
    }),
  );
}

export async function getThread(
  restaurantId: string,
  threadId: string,
): Promise<AiConversationThread> {
  return unwrapResult<AiConversationThread>(
    await aiOpenapiClient.GET('/restaurants/{restaurantId}/ai/threads/{threadId}', {
      params: { path: { restaurantId, threadId } },
    }),
  );
}

export async function updateThread(
  restaurantId: string,
  threadId: string,
  body: UpdateAiThreadBody,
): Promise<void> {
  unwrapResult<AiConversationThread>(
    await aiOpenapiClient.PATCH('/restaurants/{restaurantId}/ai/threads/{threadId}', {
      params: { path: { restaurantId, threadId } },
      body,
    }),
  );
}

export async function deleteThread(restaurantId: string, threadId: string): Promise<void> {
  unwrapResult<{ success: boolean }>(
    await aiOpenapiClient.DELETE('/restaurants/{restaurantId}/ai/threads/{threadId}', {
      params: { path: { restaurantId, threadId } },
    }),
  );
}

export async function loadThreadMessages(
  restaurantId: string,
  threadId: string,
): Promise<UIMessage[]> {
  const rows = unwrapResult<unknown[]>(
    await aiOpenapiClient.GET('/restaurants/{restaurantId}/ai/threads/{threadId}/messages', {
      params: { path: { restaurantId, threadId } },
    }),
  );
  if (rows.length === 0) return [];
  return validateUIMessages<UIMessage>({ messages: rows });
}

export async function uploadAttachment(
  restaurantId: string,
  file: File,
  onProgress?: (fraction: number) => void,
): Promise<AiAttachmentUpload> {
  const ticket = unwrapResult<AiAttachmentUploadTicket>(
    await aiOpenapiClient.POST('/restaurants/{restaurantId}/ai/attachments/presign', {
      params: { path: { restaurantId } },
      body: { fileName: file.name, mimeType: file.type, size: file.size },
    }),
  );
  await putToStorage(ticket, file, onProgress);
  return unwrapResult<AiAttachmentUpload>(
    await aiOpenapiClient.POST(
      '/restaurants/{restaurantId}/ai/attachments/{attachmentId}/complete',
      { params: { path: { restaurantId, attachmentId: ticket.attachmentId } } },
    ),
  );
}

function putToStorage(
  ticket: AiAttachmentUploadTicket,
  file: File,
  onProgress?: (fraction: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', ticket.uploadUrl);
    for (const [name, value] of Object.entries(ticket.headers)) xhr.setRequestHeader(name, value);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress?.(event.loaded / event.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Upload failed (${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error('Upload failed'));
    xhr.onabort = () => reject(new Error('Upload cancelled'));
    xhr.send(file);
  });
}

export function chatErrorText(error: Error): string | undefined {
  try {
    return errorMessageOf(JSON.parse(error.message));
  } catch {
    return undefined;
  }
}

export function errorMessageOf(body: unknown): string | undefined {
  if (typeof body !== 'object' || body === null || !('message' in body)) return undefined;
  return typeof body.message === 'string' ? body.message : undefined;
}
